import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField
} from '@mui/material';
import api from '../services/api';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const Resellers = () => {
  const navigate = useNavigate();
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResellers, setTotalResellers] = useState(0);  // Changed from totalClients
  const [onDate, setOnDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created');
  const [order, setOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResellers();
  }, []);

  const fetchResellers = async () => {
    try {
      const response = await api.get('/superreseller/list');
      setResellers(response.data.data);
      setTotalResellers(response.data.count);  // Use count field for total resellers
      setOnDate(response.data.onDate);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching resellers:', error);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const filteredResellers = resellers.filter(reseller =>
    Object.values(reseller).some(value =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedResellers = React.useMemo(() => {
    const comparator = (a, b) => {
      if (b[orderBy] < a[orderBy]) {
        return order === 'asc' ? 1 : -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    };

    return [...filteredResellers].sort(comparator);
  }, [filteredResellers, order, orderBy]);

  const headCells = [
    { id: 'created', label: 'Created' },
    { id: 'name', label: 'Name' },
    { id: 'credits', label: 'Credits', numeric: true },
    { id: 'description', label: 'Description' },
  ];

  const handleResellerClick = (resellerName) => {
    navigate(`/update-reseller/${resellerName}`);
  };

  return (
    <Box sx={{ width: '100%', padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Resellers
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1">Total Resellers: {totalResellers}</Typography>  {/* Changed from Total Clients */}
        <Typography variant="body1">Date: {moment(onDate).format('YYYY-MM-DD HH:mm:ss')}</Typography>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: 300 }}
        />
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader aria-label="resellers table">
                <TableHead>
                  <TableRow>
                    {headCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        sortDirection={orderBy === headCell.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : 'asc'}
                          onClick={() => handleRequestSort(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedResellers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((reseller, index) => (
                      <TableRow hover key={reseller.accountId}>
                        <TableCell>{moment(reseller.created).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                        <TableCell 
                          onClick={() => handleResellerClick(reseller.name)}
                          style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                        >
                          {reseller.name}
                        </TableCell>
                        <TableCell align="right">{reseller.credits}</TableCell>
                        <TableCell>{reseller.description}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredResellers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Resellers;